import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Options } from '@angular-slider/ngx-slider';
import { IPermissions } from '@models/Configuration/IPermissions';
import { Benefit } from '@models/Sessions/Idea/Benefit';
import { Challenge } from '@models/Sessions/Idea/Challenge';
import { Collaborator } from '@models/Sessions/Idea/Collaborator';
import { Comment } from '@models/Sessions/Idea/Comment';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Solution } from '@models/Sessions/Idea/Solution';
import { Vote } from '@models/Sessions/Idea/Vote';
import { Session } from '@models/Sessions/Session';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { AssetsPipe } from '@pipes/assets.pipe';
import { BusService } from '@services/bus.service';
import { ConfigService, IInputLengths } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { ExFinderService } from '@services/exfinder/exfinder.service';
import { IdeaRequestService } from '@services/sessions/idea.request.service';
import { matchPossibleCollaborators } from '@utils/ideaFilters';
import _ from 'lodash';
import { ConfirmationService, MessageService } from 'primeng/api';

import { NewIdeaModalComponent } from '../new-idea-modal/new-idea-modal.component';
import { AddCollaboratorModalComponent } from './add-collaborator-modal/add-collaborator-modal.component';
import { CollabInviteModalComponent } from './collab-invite-modal/collab-invite-modal.component';
import { CommentListComponent } from './comment-list/comment-list.component';
import { IdeaMovesModalComponent } from './idea-moves-modal/idea-moves-modal.component';
import { SuggestCollabModalComponent } from './suggest-collab-modal/suggest-collab-modal.component';

interface Slider {
  options: Options;
  value: number;
}

@Component({
  selector: 'app-idea-modal',
  templateUrl: './idea-modal.component.html',
  styleUrls: ['./idea-modal.component.scss'],
})
export class IdeaModalComponent implements OnInit, OnDestroy {
  @Input() session: Session;
  @Input() idea: Idea;
  @Input() compactView = false;
  @ViewChild(CommentListComponent) commentList: CommentListComponent;
  @ViewChild(NgbNav, { static: true }) tabset: NgbNav;

  ratingTransmitted = false;
  permissions: IPermissions;
  inputLengths: IInputLengths;

  activeTabIndex: number;

  comment: any = {};
  voting: Vote = { x: undefined, y: undefined };

  sliderX: Slider;
  sliderY: Slider;
  sliderRefresh: EventEmitter<void> = new EventEmitter<void>();

  challenge: any = {};
  benefit: any = {};
  newCollaborator = {};
  solution = { text: '' };
  backlink: any;

  constructor(
    public activeModal: NgbActiveModal,
    private appConfig: ConfigService,
    private bus: BusService,
    private events: EventsService,
    private ideaRequest: IdeaRequestService,
    public cdref: ChangeDetectorRef,
    public modalService: NgbModal,
    private exfinder: ExFinderService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private assetsPipe: AssetsPipe,
  ) {
    this.permissions = appConfig.permissions;
    this.inputLengths = appConfig.inputLengths;

    this.sliderX = this.initSlider(0);
    this.sliderY = this.initSlider(0);
  }

  lsprop(property) {
    return `idea-${this.idea._id}:${property}`;
  }

  bindData(data: {
    session?: Session;
    idea?: Idea;
    compactView?: boolean;
  }): void {
    this.session = data.session;
    this.idea = data.idea;
    this.compactView = data.compactView;
    this.bus.publish(this.events.notified.idea.modalOpened, this.idea);

    this.idea.descriptionRich =
      this.idea.descriptionRich || this.idea.description;

    (this.idea.collaboratorInvites || []).forEach(i => {
      i.user = this.session.config.participants.find(
        p => p._id.toString() === i.participant_id.toString(),
      ).user;
    });

    // Force change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.cdref.detectChanges();

    this.voting = {
      x: this.idea.voting.my.x || 0,
      y: this.idea.voting.my.y || 0,
    };

    this.sliderX = this.initSlider(this.idea.voting.my.x);
    this.sliderY = this.initSlider(this.idea.voting.my.y);

    const lsx = localStorage.getItem(this.lsprop('vote:x'));
    const lsy = localStorage.getItem(this.lsprop('vote:y'));
    if (lsx) {
      this.sliderX.value = parseFloat(lsx);
    }
    if (lsy) {
      this.sliderY.value = parseFloat(lsy);
    }

    this.sliderRefresh.emit();

    setTimeout(() => {
      if (this.isInvitedAsCollaborator()) {
        const modalRef = this.modalService.open(CollabInviteModalComponent);
        modalRef.componentInstance.idea = this.idea;
        modalRef.componentInstance.session = this.session;
        modalRef.componentInstance.acceptCb = () =>
          this.acceptCollaborationInvite();
        modalRef.componentInstance.rejectCb = () =>
          this.rejectCollaborationInvite();
      }
    }, 300);
  }

  focusComment(comment: Comment) {
    if (this.commentList) {
      this.commentList.focusComment(comment);
    }

    if (this.tabset) {
      this.tabset.navChange.subscribe(() => {
        setTimeout(() => {
          if (this.commentList) {
            this.commentList.focusComment(comment);
          }
        }, 1);
      });
      this.tabset.select('tab-1');
    }
  }

  voteChange(axis, value) {
    localStorage.setItem(this.lsprop('vote:' + axis), value);
  }

  voteSubmit() {
    return (
      this.idea &&
      (this.sliderX.value !== this.idea.voting.my.x ||
        this.sliderY.value !== this.idea.voting.my.y)
    );
  }

  commentDraft(comment) {
    if (!comment.text && !comment.parent) {
      localStorage.removeItem(this.lsprop('comment:draft'));
    } else {
      localStorage.setItem(
        this.lsprop('comment:draft'),
        JSON.stringify(comment),
      );
    }
  }

  loadCommentDraft() {
    const cd = localStorage.getItem(this.lsprop('comment:draft'));
    if (cd) {
      return JSON.parse(cd);
    }
  }

  private initSlider(value): Slider {
    return {
      value: value,
      options: {
        floor: 0,
        ceil: 10,
        step: 0.5,
        // precisionLimit: 1,
        showSelectionBar: true,
        translate: val => (+val === val ? val : ('N/A' as any)),
      },
    };
  }

  get thumbnailImage() {
    return this.assetsPipe.transform(
      this.idea.thumbnail_id || 'image/default_idea_thumbnail.png',
    );
  }

  get possibleCollaborators() {
    return this.session.config.participants
      .filter(p => p.user && p.user.registered)
      .filter(p => matchPossibleCollaborators(p, this.idea))
      .sort((a, b) => {
        if (a.user && b.user) {
          return ('' + a.user.name).localeCompare('' + b.user.name);
        }
        return 0;
      });
  }

  get participantDict() {
    return _.keyBy(this.session.config.participants, '_id');
  }

  submitRating() {
    const vote: Vote = {
      x: this.sliderX.value || 0,
      y: this.sliderY.value || 0,
    };
    this.bus.publish(
      this.events.requested.action.session.idea.vote,
      vote,
      this.idea,
      this.session,
    );
    this.ratingTransmitted = true;
    this.idea.voting.my = vote;
    localStorage.removeItem(this.lsprop('vote:x'));
    localStorage.removeItem(this.lsprop('vote:y'));

    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'idea',
      'rated',
      this.idea._id,
    );
  }

  follow() {
    // this.bus.publish(this.events.requested.action.session.idea.follow, this.idea, this.session);

    this.ideaRequest.follow(this.idea, this.session).subscribe(() => {
      this.idea.i_follow = true;

      this.bus.publish(
        this.events.received.action.session.idea.follow.success,
        { idea: this.idea },
      );
    });
    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'idea',
      'followed',
      this.idea._id,
    );
  }

  unfollow() {
    // this.bus.publish(this.events.requested.action.session.idea.unfollow, this.idea, this.session);
    this.ideaRequest.unfollow(this.idea, this.session).subscribe(() => {
      this.idea.i_follow = false;

      this.bus.publish(
        this.events.received.action.session.idea.unfollow.success,
        { idea: this.idea },
      );
    });
  }

  addChallenge() {
    this.bus.publish(
      this.events.requested.action.session.idea.challenge.add,
      this.challenge,
      this.idea,
      this.session,
    );

    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'idea',
      'addChallenge',
      this.challenge.text,
    );

    this.challenge = {};
  }

  addBenefit() {
    this.bus.publish(
      this.events.requested.action.session.idea.addBenefit,
      this.benefit,
      this.idea,
      this.session,
    );
    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'idea',
      'addBenefit',
      this.benefit.text,
    );

    this.benefit = {};
  }

  deleteBenefit(benefit: Benefit) {
    this.bus.publish(
      this.events.requested.action.session.idea.deleteBenefit,
      benefit,
      this.idea,
      this.session,
    );
  }

  deleteChallenge(challenge: Challenge) {
    this.bus.publish(
      this.events.requested.action.session.idea.challenge.delete,
      challenge,
      this.idea,
      this.session,
    );
  }

  deleteCollaborator(collaborator: Collaborator) {
    this.bus.publish(
      this.events.requested.action.session.idea.deleteCollaborator,
      collaborator,
      this.idea,
      this.session,
    );
  }

  publish() {
    this.bus.publish(
      this.events.requested.action.session.idea.publish,
      this.idea,
      this.session,
    );
  }

  unpublish() {
    this.bus.publish(
      this.events.requested.action.session.idea.unpublish,
      this.idea,
      this.session,
    );
  }

  dismiss() {
    this.activeModal.dismiss();

    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'nav',
      'close idea',
      this.idea.title,
    );
  }

  proposeSolution(challenge: Challenge) {
    this.bus.publish(
      this.events.requested.action.session.idea.challenge.solution.propose,
      this.solution,
      challenge,
      this.idea,
      this.session,
    );

    this.solution = { text: undefined };
  }

  acceptSolution(challenge: Challenge, solution: Solution) {
    this.bus.publish(
      this.events.requested.action.session.idea.challenge.solution.accept,
      solution,
      challenge,
      this.idea,
      this.session,
    );

    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'idea',
      'acceptSolution',
      challenge.text + ' ' + solution.text,
    );
  }

  unacceptSolution(challenge: Challenge, solution: Solution) {
    this.bus.publish(
      this.events.requested.action.session.idea.challenge.solution.unaccept,
      solution,
      challenge,
      this.idea,
      this.session,
    );
  }

  deleteSolution(challenge: Challenge, solution: Solution) {
    this.bus.publish(
      this.events.requested.action.session.idea.challenge.solution.delete,
      solution,
      challenge,
      this.idea,
      this.session,
    );
  }

  postMature() {
    this.bus.publish(
      this.events.requested.action.session.idea.setMature,
      this.idea.mature,
      this.idea,
      this.session,
    );
  }

  deleteIdea() {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the <strong>${this.idea.title}</strong> Project?`,
      header: 'Confirmation',
      closeOnEscape: true,
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      accept: () => {
        this.bus.publish(
          this.events.requested.action.session.idea.delete,
          this.idea,
          this.session,
        );
        this.activeModal.close();
      },
    });
  }

  // Modals

  openNewIdeaModal() {
    const modalRef = this.modalService.open(NewIdeaModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });

    modalRef.componentInstance.session = this.session;
    modalRef.componentInstance.forkIdea = this.idea;
  }

  openAddCollaboratorModal() {
    const modalRef = this.modalService.open(AddCollaboratorModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });

    modalRef.componentInstance.session = this.session;
    modalRef.componentInstance.idea = this.idea;
  }

  openCollaboratorSuggestionModal() {
    this.exfinder
      .suggestCollaborators(this.session, this.idea)
      .subscribe(suggestions => {
        const modalRef = this.modalService.open(SuggestCollabModalComponent, {
          size: 'lg',
          windowClass: 'big-modal',
        });
        modalRef.componentInstance.session = this.session;
        modalRef.componentInstance.idea = this.idea;
        modalRef.componentInstance.suggestions = suggestions;
      });
  }

  requestToBeCollaborator() {
    this.bus.publish(
      this.events.requested.action.session.idea.collaborationRequested,
      this.session.me,
      this.idea,
      this.session,
    );

    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'idea',
      'collaborationRequest',
      this.idea._id,
    );
  }

  removeCollabInvite(invite) {
    this.ideaRequest.deleteCollaborationInvite(
      invite.participant_id,
      this.idea,
      this.session,
    );
  }

  get collaborationRequestors() {
    if (!this.idea.collaboratorRequests) {
      return [];
    }
    const openRequestsIds = this.idea.collaboratorRequests.map(
      r => r.participant_id,
    );
    const requestors = this.session.config.participants.filter(p =>
      openRequestsIds.includes(p._id),
    );
    return requestors;
  }

  approveCollaboratorRequest(participant) {
    this.bus.publish(
      this.events.requested.action.session.idea.collaborationApproved,
      participant,
      this.idea,
      this.session,
    );
  }

  currentUserHasRequestedCollaboration() {
    if (this.idea.collaboratorRequests) {
      return this.idea.collaboratorRequests
        .map(r => r.participant_id)
        .includes(this.session.me._id);
    } else {
      return false;
    }
  }

  isInvitedAsCollaborator() {
    return (this.idea.collaboratorInvites || [])
      .map(r => r.participant_id)
      .includes(this.session.me._id);
  }

  acceptCollaborationInvite() {
    const invite = this.idea.collaboratorInvites.find(
      r => r.participant_id.toString() === this.session.me._id.toString(),
    );
    if (invite) {
      this.ideaRequest.acceptCollaborationInvite(
        this.session,
        this.idea,
        invite,
      );
    }
  }

  rejectCollaborationInvite() {
    const invite = this.idea.collaboratorInvites.find(
      r => r.participant_id.toString() === this.session.me._id.toString(),
    );
    if (invite) {
      this.ideaRequest.rejectCollaborationInvite(
        this.session,
        this.idea,
        invite,
      );
    }
  }

  showMoveDetails() {
    if (this.permissions.session.data.ideas.put(this.session, this.idea)) {
      if (this.idea.moves.length === 1) {
        this.dismiss();
        this.router.navigate(['/topics/' + this.idea.moves[0].session_id], {
          queryParams: {
            idea: this.idea.moves[0].idea_id,
          },
        });
      } else if (this.idea.moves.length > 1) {
        const mdref = this.modalService.open(IdeaMovesModalComponent);
        mdref.componentInstance.idea = this.idea;
        mdref.result.then(r => {
          if (r === 'nav') {
            this.dismiss();
          }
        });
      }
    }
  }

  ngOnInit() {
    this.ideaRequest.backlink(this.idea, this.session).subscribe(backlink => {
      if (backlink && backlink.session && backlink.idea) {
        this.backlink = backlink;
      }
    });
    this.subscribe();
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  navigateToBacklink() {
    if (this.backlink) {
      this.dismiss();
      this.router.navigate(['/topics/' + this.backlink.session._id], {
        queryParams: {
          idea: this.backlink.idea._id,
        },
      });
    }
  }

  // TODO: Refactor home.component to listen to single Topic events
  private handleDeleteSuccess() {
    this.bus.publish(this.events.requested.data.session.all);
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Project Deleted',
    });
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.received.action.session.idea.delete.success,
      this.handleDeleteSuccess,
      this,
    );
  }
  private unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.action.session.idea.delete.success,
      this.handleDeleteSuccess,
    );
  }
}
